/**
 * Thin API client for the OrbitCell marketing + admin frontend on orbitcell.io.
 *
 * Mirrors the request/response envelope used by the FastAPI backend
 * (see src/api/routes/auth.py and waitlist.py for the source of truth).
 *
 * Errors come back as either FastAPI's top-level envelope or the legacy
 * nested Pages envelope.
 *
 * `apiFetch()` normalizes both shapes into an `ApiError` so callers can
 * branch on `error.code` without re-parsing.
 */

import { parseApiErrorEnvelope } from "./apiError";

const RAW_BASE = (
  import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.PROD ? "https://api.orbitcell.io" : "")
).trim();

/**
 * Resolves the API origin.
 *
 *   - Default (recommended): leave VITE_API_BASE_URL unset. Requests go
 *     same-origin, which is exactly where Cloudflare Pages Functions
 *     intercept /api/* paths -- no CORS needed, no cross-origin cookies.
 *   - Override (advanced): set VITE_API_BASE_URL to an absolute URL when
 *     pointing the marketing frontend at a separate API host. When that host
 *     is api.orbitcell.io, callers must provide explicit clean API-host paths.
 *
 * Trailing slashes are stripped so callers can always prefix paths with
 * a leading slash.
 */
const BASE_URL = RAW_BASE.replace(/\/+$/, "");

function isPublicApiHostBase(baseUrl: string): boolean {
  try {
    return new URL(baseUrl).hostname.toLowerCase() === "api.orbitcell.io";
  } catch {
    return false;
  }
}

export function apiRoutePath(paths: { sameOrigin: string; apiHost: string }): string {
  const selected = isPublicApiHostBase(BASE_URL) ? paths.apiHost : paths.sameOrigin;
  return selected.startsWith("/") ? selected : `/${selected}`;
}

export function apiRouteUrl(paths: { sameOrigin: string; apiHost: string }): string {
  return `${BASE_URL}${apiRoutePath(paths)}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly raw: unknown;

  constructor(status: number, code: string | null, message: string, raw?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.raw = raw;
  }
}

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${BASE_URL}${normalizedPath}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers ?? {}),
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
      credentials: "include",
    });
  } catch (cause) {
    throw new ApiError(0, "network_error", "Couldn't reach the server. Check your connection.", cause);
  }

  let parsed: unknown = null;
  if (response.headers.get("content-type")?.includes("application/json")) {
    try {
      parsed = await response.json();
    } catch {
      // Empty / malformed JSON. Leave parsed as null.
    }
  }

  if (!response.ok) {
    const { code, message } = parseApiErrorEnvelope(parsed, response.status);
    throw new ApiError(response.status, code, message, parsed);
  }

  return parsed as T;
}

// ---------------------------------------------------------------------------
// Waitlist
// ---------------------------------------------------------------------------
export interface WaitlistSubmitPayload {
  work_email: string;
  company_name: string;
  ignition_user_count: "1-5" | "6-25" | "26-100" | "100+";
  user_role: string;
  industry: string;
  ignition_version: "8.1.x" | "8.3.x" | "both" | "other";
  source?: string;
  turnstile_token?: string;
}

export interface WaitlistSubmitResult {
  ok: true;
  email_sent: boolean;
}

export function submitWaitlist(
  payload: WaitlistSubmitPayload,
  signal?: AbortSignal,
): Promise<WaitlistSubmitResult> {
  return apiFetch<WaitlistSubmitResult>(
    apiRoutePath({ sameOrigin: "/api/waitlist", apiHost: "/waitlist" }),
    {
      method: "POST",
      body: payload,
      signal,
    },
  );
}

// ---------------------------------------------------------------------------
// Admin marketing-console API
// ---------------------------------------------------------------------------
export interface AdminMe {
  email: string;
  label: string | null;
}

export interface AdminWaitlistRow {
  id: number;
  work_email: string;
  company_name: string;
  ignition_user_count: string;
  user_role: string;
  industry: string;
  ignition_version: string;
  source: string | null;
  ip: string | null;
  user_agent: string | null;
  confirmed_at: string | null;
  created_at: string;
}

export interface AdminAuditRow {
  id: number;
  actor_email: string | null;
  action: string;
  resource: string | null;
  metadata: string | null;
  ip: string | null;
  created_at: string;
}

export interface AdminAnalytics {
  range: { start: string; end: string; days: number };
  daily: { date: string; visits: number; page_views: number }[];
  top_paths: { path: string; page_views: number }[];
  top_referrers: { referrer: string; page_views: number }[];
  top_countries: { country: string; page_views: number }[];
  dry_run: boolean;
}

export interface AdminEmailDraft {
  subject: string;
  body_text: string;
  body_html: string;
  model: string;
  dry_run: boolean;
  recipient_count: number;
  audience: {
    ignition_version: string | null;
    ignition_user_count: string | null;
    industry: string | null;
  };
}

export interface AdminEmailSendResult {
  ok: true;
  sent: number;
  failed: number;
  dry_run: boolean;
  test_mode: boolean;
}

export interface AdminAudienceFilter {
  ignition_version?: string | null;
  ignition_user_count?: string | null;
  industry?: string | null;
}

export const adminApi = {
  requestMagicLink(email: string) {
    return apiFetch<{ ok: boolean; message: string }>(
      apiRoutePath({
        sameOrigin: "/api/admin/auth/magic",
        apiHost: "/admin/auth/magic",
      }),
      {
        method: "POST",
        body: { email },
      },
    );
  },
  logout() {
    return apiFetch<null>(
      apiRoutePath({
        sameOrigin: "/api/admin/auth/logout",
        apiHost: "/admin/auth/logout",
      }),
      { method: "POST" },
    );
  },
  me(signal?: AbortSignal) {
    return apiFetch<AdminMe>(
      apiRoutePath({ sameOrigin: "/api/admin/me", apiHost: "/admin/me" }),
      { signal },
    );
  },
  listWaitlist(params: { limit?: number; cursor?: number } = {}, signal?: AbortSignal) {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.cursor) q.set("cursor", String(params.cursor));
    const qs = q.toString();
    const path = apiRoutePath({
      sameOrigin: "/api/admin/waitlist",
      apiHost: "/admin/waitlist",
    });
    return apiFetch<{ rows: AdminWaitlistRow[]; next_cursor: number | null; total: number }>(
      `${path}${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },
  waitlistCsvUrl(): string {
    return apiRouteUrl({
      sameOrigin: "/api/admin/waitlist?format=csv",
      apiHost: "/admin/waitlist?format=csv",
    });
  },
  listAudit(
    params: { limit?: number; cursor?: number; actor_email?: string; action_prefix?: string } = {},
    signal?: AbortSignal,
  ) {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.cursor) q.set("cursor", String(params.cursor));
    if (params.actor_email) q.set("actor_email", params.actor_email);
    if (params.action_prefix) q.set("action_prefix", params.action_prefix);
    const qs = q.toString();
    const path = apiRoutePath({
      sameOrigin: "/api/admin/audit",
      apiHost: "/admin/audit",
    });
    return apiFetch<{ rows: AdminAuditRow[]; next_cursor: number | null }>(
      `${path}${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },
  analytics(days: number, signal?: AbortSignal) {
    const path = apiRoutePath({
      sameOrigin: "/api/admin/analytics",
      apiHost: "/admin/analytics",
    });
    return apiFetch<AdminAnalytics>(`${path}?days=${days}`, { signal });
  },
  generateEmail(prompt: string, audience: AdminAudienceFilter, signal?: AbortSignal) {
    return apiFetch<AdminEmailDraft>(
      apiRoutePath({
        sameOrigin: "/api/admin/email/generate",
        apiHost: "/admin/email/generate",
      }),
      {
        method: "POST",
        body: { prompt, audience },
        signal,
      },
    );
  },
  sendEmail(args: {
    subject: string;
    body_text: string;
    body_html: string;
    audience?: AdminAudienceFilter;
    test_to?: string;
  }) {
    return apiFetch<AdminEmailSendResult>(
      apiRoutePath({
        sameOrigin: "/api/admin/email/send",
        apiHost: "/admin/email/send",
      }),
      {
        method: "POST",
        body: { ...args, confirm: true },
      },
    );
  },
};
